import * as React from 'react'

import getConfig from 'next/config'

import { Product as SdProduct } from '@grandvisionhq/analytics'
import { Products as ProductsApi } from '@grandvisionhq/api-client'
import {
  CmsComponentSlot,
  CmsHeadTags,
  CmsProductBreadcrumbs,
  useBloomreachPageRoutes,
  useBloomreachPdpContent,
  useBloomreachProductRoutes,
} from '@grandvisionhq/bloomreach'
import { AddToCart as AddToCartV2, MiniCartModal, Upsell } from '@grandvisionhq/carts-v2'
import { CommerceToolsApi } from '@grandvisionhq/commercetools'
import { NextPage, withErrorPage } from '@grandvisionhq/next'
import {
  getProductSeoInfo,
  Helpers as ProductHelpers,
  parseProductDetailQueryParams,
  ProductDetail,
  ProductDetailProvider,
  ProductFrameMeasurements,
  ProductTypes,
  RenderAddToCartButton,
  SSP,
  VtoConfigProvider,
} from '@grandvisionhq/products'
import { ReviewDisplay, ReviewsProvider } from '@grandvisionhq/reviews'
import { CrossSellProducts } from '@grandvisionhq/search'
import { DeliveryIndicatorProvider } from '@grandvisionhq/shipping'
import { useIntl } from '@grandvisionhq/state'
import { compose, getAttrValue, isString } from '@grandvisionhq/utils'

import { withCms, withLayout } from '../../cms'
import { defaultErrorProps, ErrorProps } from '../../components/error-props'
import { settings as cartSettings, withCart } from '../../components/hocs/with-cart'
import { getDeliveryIndicatorConfig } from '../../configuration'
import { getAlgoliaCrossSellConfig } from '../../util/algolia'
import {
  isBynderImagesEnabled,
  isCartV2Enabled,
  isContactLensEnabled,
  isContactLensStockEnabled,
  isCrossSellEnabled,
  isReviewsEnabled,
  isSolutionsEnabled,
  isVtoEnabled,
} from '../../util/feature-toggle'
import { getAllowedExtras } from '../../util/get-allowed-extras'

import '@grandvisionhq/reviews/dist/index.css'

const { publicRuntimeConfig } = getConfig()
const endpoints = publicRuntimeConfig?.grandvision?.endpoints || {}
const staticsUrl = publicRuntimeConfig?.grandvision?.staticsUrl || ''
const selfUrl = publicRuntimeConfig?.selfUrl || ''
const vtoConfig = publicRuntimeConfig?.ditto || {}
const sspInitialGlasses =
  publicRuntimeConfig?.productDetail?.SSP.frames.initialGlasses || 'frameAndMonofocal'
const { apiKey: powerReviewsApiKey, groupId, opcoId } = publicRuntimeConfig?.powerReviews || {}

const featureToggles: Record<string, boolean> = Object.entries({
  isSolutionsEnabled,
  isContactLensStockEnabled,
  isPrescriptionEnabled: isContactLensEnabled,
  isPrescriptionInfoEnabled: () => false,
}).reduce((result, [key, fn]) => ({ ...result, [key]: fn() }), {})

const deliveryIndicatorConfig = getDeliveryIndicatorConfig()

type ReviewProviderProps = React.ComponentProps<typeof ReviewsProvider>

type SspState = {
  selectedGlasses: 'frameOnly' | 'frameAndMonofocal' | 'frameAndMultifocal'
  totalPrice?: number
}

type InitialProps = {
  sku: string
  product?: CommerceToolsApi.ProductProjection
  extrasData?: CommerceToolsApi.ProductProjection[]
  queryParams: Record<string, string | string[]>
}

type Props = InitialProps

export const ProductDetailPage: NextPage<Props, InitialProps | ErrorProps> = ({
  sku,
  product,
  extrasData,
  queryParams,
}) => {
  const { locale } = useIntl()
  const content = useBloomreachPdpContent()
  const { routes } = useBloomreachPageRoutes()
  const [, { formatProductUrl }] = useBloomreachProductRoutes()

  /**
   * Product data, seo, cross-sell configurations
   */
  const productType = product?.productType?.obj?.name as CommerceToolsApi.ProductTypeName
  const productUrl = selfUrl + formatProductUrl({ slug: product?.slug ?? {}, productType, sku })
  const brandKey = String(getAttrValue(product?.masterVariant ?? {}, 'brandKey'))
  const seoInfo = getProductSeoInfo(staticsUrl, locale, product, undefined, isBynderImagesEnabled())
  const crossSellConfig = React.useMemo(() => getAlgoliaCrossSellConfig(productType), [productType])
  const vtoBanner = content.vtoBanner as ProductTypes.BloomreachBanner

  /**
   * Product type checks
   */
  const isLenses = React.useMemo(() => CommerceToolsApi.isLenses(productType), [productType])
  const isSunglasses = React.useMemo(
    () => CommerceToolsApi.isSunglasses(productType),
    [productType]
  )
  const isFrames = React.useMemo(() => CommerceToolsApi.isFrames(productType), [productType])

  /**
   * PowerReviews setup
   */
  const reviewsInitialId = isLenses ? brandKey : product?.masterVariant.sku
  const reviewsInitialSku = isLenses ? undefined : sku // SKU is not required for CL
  const reviewsProviderProps = React.useMemo<ReviewProviderProps>(
    () => ({
      enabled: isReviewsEnabled() && !!product?.reviewEnabled,
      config: {
        groupId,
        opcoId,
        apiKey: powerReviewsApiKey,
        writeReviewPath: routes['write-review'],
      },
    }),
    // other fields are static during runtime
    [product, routes]
  )

  /**
   * SSP setup
   */
  const sspEnabled = isFrames && !!extrasData
  const [sspState, setSspState] = React.useState<SspState>(() => ({
    selectedGlasses: 'frameAndMonofocal',
  }))
  const handleSspChange = React.useCallback((newState: SspState) => {
    setSspState((state) => ({ ...state, ...newState }))
  }, [])

  if (!product) {
    console.error(`Failed to render ProductDetailPage because of invalid product data: ${product}`)
    return null
  }

  /**
   * Cart v2 setup
   */
  const renderAddToCartButton: RenderAddToCartButton | undefined = isCartV2Enabled()
    ? ({ caption, disabled, lineItemDrafts, onSuccess }) => (
        <AddToCartV2
          caption={caption}
          disabled={disabled}
          onSuccess={onSuccess}
          lineItemDrafts={lineItemDrafts}
        />
      )
    : undefined

  /**
   * Cart v2 upsell setup (optional)
   */
  const productCatKey = getAttrValue(product.masterVariant, 'recommendedProductCatKey')
  const recommendedCategoryKey = isString(productCatKey) ? productCatKey : undefined
  const params = parseProductDetailQueryParams(queryParams)

  return (
    <>
      <CmsHeadTags
        title={seoInfo.title}
        description={seoInfo.description}
        ogImage={seoInfo.imageUrl}
        ogUrl={productUrl}
      >
        {isReviewsEnabled() && <link rel="preconnect" href="https://ui.powerreviews.com" />}
      </CmsHeadTags>

      <CmsComponentSlot path="main/top-container" />
      <DeliveryIndicatorProvider config={deliveryIndicatorConfig}>
        <ProductDetailProvider
          product={product}
          sku={sku}
          routes={routes}
          productType={productType}
          locale={locale}
          staticsUrl={staticsUrl}
          renderAddToCartButton={renderAddToCartButton}
          {...params}
        >
          <CmsProductBreadcrumbs product={product} sku={sku} />
          <ReviewsProvider {...reviewsProviderProps}>
            <VtoConfigProvider enabled={isVtoEnabled()} config={vtoConfig}>
              <ProductDetail
                colorFeatureIcons="orange"
                disclaimer={content.disclaimer}
                formatProductUrl={formatProductUrl}
                maxOrderableQuantity={cartSettings.maximumQuantity}
                packshotTypes={content.packshotTypes}
                price={sspState.totalPrice}
                productType={productType}
                productTypeUsps={content.usps}
                vtoBanner={vtoBanner}
                {...featureToggles}
              >
                {sspEnabled && (
                  <SSP
                    product={product}
                    initialGlasses={sspInitialGlasses}
                    lensExtras={extrasData ?? []}
                    lensExtrasResolver={getAllowedExtras}
                    onChange={handleSspChange}
                    useIncludedFrame={false}
                    useIncludedLensPack={true}
                  />
                )}
              </ProductDetail>
            </VtoConfigProvider>

            {(isFrames || isSunglasses) && <ProductFrameMeasurements product={product} />}

            {isCrossSellEnabled() && (
              <CrossSellProducts
                algoliaConfig={crossSellConfig}
                formatProductUrl={formatProductUrl}
              />
            )}

            {isReviewsEnabled() && (
              <ReviewDisplay productId={reviewsInitialId} variantId={reviewsInitialSku} />
            )}

            <div className="product-detail-layout__components">
              <CmsComponentSlot path="main/bottom-container" />
            </div>

            {isCartV2Enabled() ? (
              <MiniCartModal links={{ cart: routes.cart, checkout: routes.checkout }}>
                {recommendedCategoryKey
                  ? (addedLineItems) => (
                      <Upsell
                        recommendedCategoryKey={recommendedCategoryKey}
                        lineItems={addedLineItems}
                      />
                    )
                  : null}
              </MiniCartModal>
            ) : null}

            <SdProduct
              product={product}
              activeSKU={sku}
              locale={locale}
              staticsUrl={staticsUrl}
              url={productUrl}
            />
          </ReviewsProvider>
        </ProductDetailProvider>
      </DeliveryIndicatorProvider>
    </>
  )
}

ProductDetailPage.getInitialProps = async ({ query, asPath = '' }) => {
  const [, slug] = /.+\/(.+)\/([^?]+)(\?.*)?$/.exec(asPath) ?? []

  if (!slug) {
    console.log(`Can't extract slug and sku from ${asPath}, got slug: '${String(slug)}'`)
    return {
      ...defaultErrorProps,
      statusCode: 404,
    }
  }

  let product: CommerceToolsApi.ProductProjection | undefined
  try {
    product = await ProductsApi.getProductBySlug(endpoints.products, { slug })
  } catch (err: any) {
    // Non OK product service api response, return props to show the error page
    console.error(`Failed to fetch product with slug: ${String(slug)}`, err)
    return {
      ...defaultErrorProps,
      statusCode: err.statusCode,
    }
  }

  // Fetch SSP information
  let extrasData: CommerceToolsApi.ProductProjection[] | undefined
  const extrasId = ProductHelpers.getExtrasId(product)
  if (extrasId) {
    const { results } = await ProductsApi.query(endpoints.products, {
      filters: [`productType.id:"${extrasId}"`],
    })
    extrasData = results
  }

  return {
    sku: product?.masterVariant?.sku ?? '',
    product,
    extrasData,
    queryParams: query as Record<string, string | string[]>,
  }
}

export default compose(
  withCms(),
  withErrorPage,
  withLayout(),
  withCart({ readOnly: true })
)(ProductDetailPage)
